import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from '@material-ui/core';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import { Pagination } from '@material-ui/lab';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { GetDeleteProjectDocuments } from '../../api/delete-project-documents';
import { RootState } from '../../app/store';
import { useAppDispatch } from '../../hooks/useAppRudux';
import Breadcrumb from '../Breadcrumb';
import { getProjectAsync } from '../Project/projectSlice';
import { getDeleteProjectDocumentsAsync } from './deleteProjectDocumentSlice';
import moment from 'moment';

interface Pagination {
  page: number;
  limit: number;
}

export default function DeleteDocumentView() {
  const classes = useStyles();
  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  const params = useParams<{ projectId: string }>();
  const projectId = Number.parseInt(params.projectId, 10);

  const { selectedProject } = useSelector((state: RootState) => state.projects);
  const { data: deleteDocuments, count } = useSelector(
    (state: RootState) => state.deleteProjectDocuments
  );

  const [pagination, setPagination] = useState<Pagination>({
    page: 0,
    limit: 20
  });

  useEffect(() => {
    loadingData(pagination);
  }, [projectId]);

  useEffect(() => {
    if (projectId) {
      dispatch(getProjectAsync(projectId));
    }
  }, []);

  function loadingData(pagination: Pagination): void {
    if (projectId) {
      const deleteProjectDocuments: GetDeleteProjectDocuments = {
        projectId,
        page: pagination.page,
        limit: pagination.limit
      };
      dispatch(getDeleteProjectDocumentsAsync(deleteProjectDocuments));
    }
  }

  function handleChangePage(
    event: React.ChangeEvent<unknown>,
    value: number
  ): void {
    const newPagination = { ...pagination, page: value - 1 };
    loadingData(newPagination);
    setPagination(newPagination); // The first value of pagination is 1, not 0

    if (pagination.page !== value - 1)
      window['scrollTo']({ top: 0, behavior: 'smooth' });
  }

  return (
    <div className={classes.root}>
      <Breadcrumb
        name="deletedDocument"
        data={{
          projectName: selectedProject?.name,
          projectId: selectedProject?.id
        }}
      />
      <Typography className={classes.title} component="p" variant="h4">
        {t('deleteProjectDocument.title')}
      </Typography>
      <TableContainer className={classes.paper} component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>{t('deleteProjectDocument.table.documentName')}</TableCell>
              <TableCell>{t('deleteProjectDocument.table.documentNo')}</TableCell>
              <TableCell>{t('deleteProjectDocument.table.documentRevisions')}</TableCell>
              <TableCell>{t('deleteProjectDocument.table.lastStepReviewing')}</TableCell>
              <TableCell>{t('deleteProjectDocument.table.reasonForDeletion')}</TableCell>
              <TableCell>{t('deleteProjectDocument.table.deletedBy')}</TableCell>
              <TableCell>{t('deleteProjectDocument.table.deletedDate')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {deleteDocuments.length > 0 ? (
              deleteDocuments.map((document, index) => {
                return (
                  <TableRow key={index}>
                    <TableCell>{document.name}</TableCell>
                    <TableCell>{document.no}</TableCell>
                    <TableCell>{document.revisions}</TableCell>
                    <TableCell>{document.lastStepReview}</TableCell>
                    <TableCell>{document.reason}</TableCell>
                    <TableCell>
                      {document.firstName} {document.lastName}
                    </TableCell>
                    <TableCell>
                      {moment(document.createdAt).format('lll')}
                    </TableCell>
                  </TableRow>
                );
              })
            ) : (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  {t('deleteProjectDocument.empty')}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <div className={classes.pagination}>
        <Pagination
          count={Math.ceil(count / pagination.limit)}
          shape="rounded"
          page={pagination.page + 1}
          onChange={handleChangePage}
        />
      </div>
    </div>
  );
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: { margin: theme.spacing(1.5, 0, 12, 0) },
    title: {
      margin: theme.spacing(1.5, 0, 2, 0),
      [theme.breakpoints.down('xs')]: {
        textAlign: 'center',
        margin: theme.spacing(3.5, 0, 4, 0)
      }
    },
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center'
    },
    paper: {
      marginBottom: theme.spacing(2),
      borderRadius: theme.spacing(2)
    },
    pagination: {
      display: 'flex',
      justifyContent: 'flex-end'
    }
  })
);
